import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { Star, X } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { submitSellerRating } from '../services/api.shop';
import { ValidationError } from './ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { useLoginPrompt } from '../loginPrompts/loginPromptContext';

export interface RatingPopupProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: number;
    productTitle?: string;
    productImageUrl?: string;
    onSubmitted?: () => void;
    /** Called when user skips or closes without submitting (not after success). */
    onSkipOrClose?: () => void;
}

type Step = 'select' | 'confirm' | 'done';

const RatingPopup: React.FC<RatingPopupProps> = ({
    isOpen,
    onClose,
    orderId,
    productTitle,
    productImageUrl,
    onSubmitted,
    onSkipOrClose,
}) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queue = useLoginPrompt();
    const queueRef = useRef(queue);
    queueRef.current = queue;
    const finishedQueueRef = useRef(false);

    useEffect(() => {
        if (!isOpen) return;
        finishedQueueRef.current = false;
        const api = queueRef.current;
        const token = `rating-dialog:${orderId}`;
        api.holdOverlay(token);
        return () => api.releaseOverlay(token);
    }, [isOpen, orderId]);

    const finishQueue = () => {
        if (finishedQueueRef.current) return;
        finishedQueueRef.current = true;
        const api = queueRef.current;
        if (api.getGrantedId() === 'rating') api.settle();
        else api.stampClosed();
    };

    const [step, setStep] = useState<Step>('select');
    const [stars, setStars] = useState(0);
    const [hoverStars, setHoverStars] = useState(0);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const submittedRef = React.useRef(false);

    const resetState = () => {
        setStep('select');
        setStars(0);
        setHoverStars(0);
        setComment('');
        setError(null);
        setLoading(false);
    };

    const dismissWithoutRating = () => {
        if (!submittedRef.current) {
            onSkipOrClose?.();
        }
        finishQueue();
        resetState();
        submittedRef.current = false;
        onClose();
    };

    const resetAndClose = () => {
        // Success path closes without creating a reminder
        finishQueue();
        resetState();
        submittedRef.current = false;
        onClose();
    };

    const handleContinue = () => {
        if (stars < 1) {
            setError(tr('rating.popup.starsRequired', 'Please select a star rating.'));
            scrollToFirstErrorField({ stars: true });
            return;
        }
        setError(null);
        setStep('confirm');
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            await submitSellerRating({
                orderId,
                stars,
                comment: comment.trim() || undefined,
            });
            submittedRef.current = true;
            setStep('done');
            onSubmitted?.();
            setTimeout(() => resetAndClose(), 1400);
        } catch (err: unknown) {
            let msg = tr('rating.popup.error', 'Could not submit rating. Please try again.');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) {
                    msg = data.message.includes('already')
                        ? tr('rating.popup.already', 'You already rated this order.')
                        : data.message;
                }
            }
            setError(msg);
            setStep('select');
            scrollToFirstErrorField({ stars: true });
        } finally {
            setLoading(false);
        }
    };

    const displayStars = hoverStars || stars;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => { if (step !== 'done') dismissWithoutRating(); }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[var(--bg-color)] shadow-2xl transition-all border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)]">
                                <div className="flex items-center justify-between px-5 pt-5 pb-2">
                                    <Dialog.Title className="text-lg font-semibold text-[var(--text-color)]">
                                        {tr('rating.popup.title', 'Rate your experience')}
                                    </Dialog.Title>
                                    {step !== 'done' && (
                                        <button
                                            type="button"
                                            onClick={dismissWithoutRating}
                                            className="rounded-full p-1.5 hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)]"
                                            aria-label="Close"
                                        >
                                            <X className="h-5 w-5 text-[var(--text-color)]" />
                                        </button>
                                    )}
                                </div>

                                <div className="px-5 pb-6 pt-2">
                                    {(productTitle || productImageUrl) && (
                                        <div className="mb-4 flex items-center gap-3 rounded-xl bg-[color-mix(in_srgb,var(--bg-color),white_12%)] p-3 border border-[color-mix(in_srgb,var(--accent-color),transparent_70%)]">
                                            {productImageUrl && (
                                                <img
                                                    src={productImageUrl}
                                                    alt=""
                                                    className="h-14 w-14 rounded-lg object-cover"
                                                />
                                            )}
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-[var(--text-color)] truncate">
                                                    {productTitle}
                                                </p>
                                                <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                    #{orderId}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {step === 'select' && (
                                        <>
                                            <p className="mb-3 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                                                {tr('rating.popup.subtitle', 'How was this purchase?')}
                                            </p>
                                            <div
                                                data-error-field="stars"
                                                className="mb-4 flex justify-center gap-1"
                                            >
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        onMouseEnter={() => setHoverStars(n)}
                                                        onMouseLeave={() => setHoverStars(0)}
                                                        onClick={() => {
                                                            setStars(n);
                                                            setError(null);
                                                        }}
                                                        className="p-1 transition-transform hover:scale-110 focus:outline-none"
                                                        aria-label={`${n} stars`}
                                                    >
                                                        <Star
                                                            className={`h-9 w-9 ${
                                                                n <= displayStars
                                                                    ? 'fill-[var(--accent-color)] text-[var(--accent-color)]'
                                                                    : 'text-[color-mix(in_srgb,var(--text-color),transparent_60%)]'
                                                            }`}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                            <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                                                {tr('rating.popup.comment', 'Add a short review (optional)')}
                                            </label>
                                            <textarea
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                                                rows={3}
                                                maxLength={500}
                                                className="mb-1 w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40"
                                                placeholder="..."
                                            />
                                            <p className="mb-2 text-right text-xs text-[color-mix(in_srgb,var(--text-color),transparent_50%)]">
                                                {comment.length}/500
                                            </p>
                                            <div className="mb-3">
                                                <ValidationError error={error} />
                                            </div>
                                            <button
                                                type="button"
                                                disabled={stars < 1}
                                                onClick={handleContinue}
                                                className="w-full rounded-xl bg-[var(--accent-color)] py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-40"
                                            >
                                                {tr('rating.popup.continue', 'Continue')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={dismissWithoutRating}
                                                className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] py-2.5 text-sm font-medium text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]"
                                            >
                                                {tr('rating.popup.skip', 'Skip for now')}
                                            </button>
                                            <Link
                                                to={`/helpdesk/order-issue?orderId=${orderId}`}
                                                onClick={dismissWithoutRating}
                                                className="mt-4 block text-center text-sm text-[var(--accent-color)] underline hover:opacity-80"
                                            >
                                                {tr('rating.popup.issues', 'Having issues with this order?')}
                                            </Link>
                                        </>
                                    )}

                                    {step === 'confirm' && (
                                        <>
                                            <div className="mb-4 flex justify-center gap-1">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star
                                                        key={n}
                                                        className={`h-8 w-8 ${
                                                            n <= stars
                                                                ? 'fill-[var(--accent-color)] text-[var(--accent-color)]'
                                                                : 'text-[color-mix(in_srgb,var(--text-color),transparent_60%)]'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                            <p className="mb-6 text-center text-sm text-[var(--text-color)]">
                                                {tr('rating.popup.confirm', 'Submit {stars}★ rating? You can\'t change this later.').replace(
                                                    '{stars}',
                                                    String(stars)
                                                )}
                                            </p>
                                            <div className="mb-3">
                                                <ValidationError error={error} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    disabled={loading}
                                                    onClick={() => setStep('select')}
                                                    className="flex-1 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] py-2.5 text-sm font-medium text-[var(--text-color)]"
                                                >
                                                    {tr('rating.popup.back', 'Back')}
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={loading}
                                                    onClick={handleSubmit}
                                                    className="flex-1 rounded-xl bg-[var(--accent-color)] py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                                >
                                                    {loading
                                                        ? '...'
                                                        : tr('rating.popup.submit', 'Confirm rating')}
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {step === 'done' && (
                                        <div className="py-6 text-center">
                                            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-color)]/30">
                                                <Star className="h-6 w-6 fill-[var(--accent-color)] text-[var(--accent-color)]" />
                                            </div>
                                            <p className="text-base font-semibold text-[var(--text-color)]">
                                                {tr('rating.popup.success', 'Thanks for your feedback!')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default RatingPopup;
